import { useQuery } from '@tanstack/react-query';
import { projectsApi } from '@/lib/api';
import { useAuthStore, getIsProjectAdmin, getIsProjectStaff } from '@/stores/auth.store';

/**
 * The caller's standing in one project, from the shared ['project', id] query:
 *
 * - isProjectAdmin — settings, members, archive, links, agency-only lists:
 *   Solid admins on every project, a partner agency's admins on projects their
 *   agency owns.
 * - isProjectStaff — staff views (agency-only lists, staff panels): Solid staff
 *   on every project, a partner agency's people on their agency's projects.
 *
 * For Solid users these equal getIsAgencyAdmin / getIsAgencyMember exactly.
 * While the project loads, partner roles read false (fail closed).
 */
export function useProjectRoles(projectId?: string) {
  const user = useAuthStore((s) => s.user);
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId!),
    enabled: !!projectId,
  });
  const orgId = (project as { organizationId?: string } | undefined)?.organizationId;
  return {
    isProjectAdmin: getIsProjectAdmin(user, orgId),
    isProjectStaff: getIsProjectStaff(user, orgId),
  };
}
